import React, { useEffect, useState, useRef } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { FaSearch, FaMicrophone } from "react-icons/fa";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import "./HomeSearchInput.css";

const HomeSearchInput = ({
  query,
  setQuery,
  darkMode,
  suggestions,
  setManualTrigger,
  handleNavigateViewAllTemples,
  from,
  setFilters,
  emptyFilters,
  setAppliedFilterCount,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showMicPopup, setShowMicPopup] = useState(false);

  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsFocused(false);
        setShowMicPopup(false);
        handleStopListening();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (transcript) setQuery(transcript);
  }, [transcript]);

  useEffect(() => {
    if (!listening && transcript) {
      triggerSearch(transcript);
      setShowMicPopup(false);
    }
  }, [listening]);

  const handleStartListening = () => {
    setIsFocused(false);
    resetTranscript();
    setShowMicPopup(true);

    SpeechRecognition.startListening({
      continuous: false,
      language: "en-US",
    }).catch(() => setShowMicPopup(false));
  };

  const handleStopListening = () => {
    SpeechRecognition.stopListening();
    setShowMicPopup(false);
  };

  const triggerSearch = (val) => {
    const finalValue = val || query;

    setQuery(finalValue);

    if (from === "DASHBOARD") {
      handleNavigateViewAllTemples(finalValue);
    } else {
      setIsFocused(false);
      if (setManualTrigger) setManualTrigger((prev) => prev + 1);
      setAppliedFilterCount(0);
      setFilters(emptyFilters);
    }
  };

  const placeholders = [
    "Ask anything about temples, deities, or pilgrimages…",
    "Looking for a temple? Try searching by name, deity, location",
    "Discover stories and facts about India’s sacred temples…",
    "Start your spiritual journey — ask anything divine…",
  ];

  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  if (!browserSupportsSpeechRecognition) {
    return <span>Your browser does not support speech recognition.</span>;
  }

  const handleInputChange = (value) => {
    // basic cleanup – remove repeated spaces
    value = value.replace(/\s{2,}/g, " ");
    // 1. Remove Unicode emojis
    value = value.replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|\u24C2|\uD83C[\uDDE6-\uDDFF]{1,2}|[\u2600-\u26FF]\uFE0F?|[\u200D])/g,
      ""
    );

    // 2. Remove kaomoji-like patterns (e.g., (´◡`), (╯°□°)╯)
    value = value.replace(/\([^\)]+\)/g, ""); // removes anything inside parentheses

    // 3. Remove leading spaces or punctuation
    value = value.replace(/^[.,\/?\s]+/, "");

    // 4. Replace multiple spaces with a single space
    value = value.replace(/\s{2,}/g, " ");

    // 5. Replace repeated punctuation with single occurrence
    value = value.replace(/([.,\/?])\1+/g, "$1");

    // 6. Remove decorative/fancy symbols (like ☆, ✿, ｡, ＊, ★)
    value = value.replace(
      /[☆★✿｡･※•✧✩❀❁❃❇❈❉❊❋⁂⌘❖✣✤✥✦✧✩✪✫✬✭✮✯✰✱✲✳✴✵✶✷✸✹✺✻✼✽✾✿❀❁❂❃❄❅❆❇❈❉❊❋❖❘❙❚❝❞❡❢❣❤❥❦❧∘◦⊙○◉◎⊛⊚⊗⊘⊕⊖⊞⊟⊠⊡⊢⊣⊤⊥⊿⋆⋇⋈⋉⋊⋋⋌⋍⋎⋏⋐⋑⋒⋓⋔⋕⋖⋗⋘⋙⋚⋛⋜⋝⋞⋟⋠⋡⋢⋣⋤⋥⋦⋧⋨⋩⋪⋫⋬⋭⋮⋯⋰⋱⋲⋳⋴⋵⋶⋷⋸⋹⋺⋻⋼⋽⋾⋿※]/g,
      ""
    );
    setIsFocused(true);
    setQuery(value);
  };

  return (
    <div
      ref={wrapperRef}
      className={`search-container ${darkMode ? "dark" : "light"} mt-6`}
    >
      <div
        className={`search-box ${
          isFocused || query.length > 0 ? "focused" : ""
        }`}
      >
        {/* LEFT SEARCH ICON */}
        <div className="icon-search" onClick={() => triggerSearch()}>
          {/* <FaSearch className="icon-search-size" />*/}
          <AutoAwesomeIcon className="icon-search-size" />
          {/* <i class="fa-sharp fa-solid fa-sparkles"></i>  */}
        </div>

        {/* INPUT FIELD */}
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholders[placeholderIndex]}
          value={query}
          onFocus={() => setIsFocused(true)}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && triggerSearch()}
        />

        {/* MICROPHONE ICON */}
        <div className="icon-wrapper">
          <FaMicrophone
            onClick={handleStartListening}
            className="mic-icon-search"
          />
        </div>

        {/* AI Enabled */}
        {/* <div class="ai-mode-button">
          <span class="tick">✔</span> AI Enabled
        </div> */}
      </div>

      {/* SUGGESTION LIST */}
      {/* {isFocused && suggestions.length > 0 && !isClicked && ( */}
      <div
        className={`suggestions ${isFocused ? "focused" : "hidden"}`}
        aria-hidden={!isFocused}
      >
        <div className="suggestion-list">
          {suggestions && suggestions.length > 0 ? (
            suggestions.map((item, index) => (
              <div
                key={index}
                className="suggestion-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  // setQuery(item);
                  triggerSearch(item);
                }}
              >
                {/* replace with your spark icon if needed */}
                <FaSearch size={20} className="suggestion-icon" />
                <span>{item}</span>
              </div>
            ))
          ) : (
            <div className="suggestion-item no-result">No suggestions</div>
          )}
        </div>
      </div>

      {/* MICROPHONE POPUP */}
      {showMicPopup && (
        <div className="mic-overlay">
          <div className="mic-popup">
            <button className="mic-close-btn" onClick={handleStopListening}>
              ✖
            </button>

            <FaMicrophone size={40} className="mic-icon" />
            <p>Speak now…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeSearchInput;
